/**
 * Admission API (EPIC C / C1) — 1:1 with the frozen §2 API CONTRACT
 * (state/epics/C/C1/build-plan.md). Every route lives under the gated mount
 * `/api/organisation` (server fires `require_admission_enabled` first → 403
 * ADMISSION_DISABLED for dark orgs).
 *
 * Error envelope (existing IPD convention): `detail = {code, message, ...extras}`.
 * Each function returns `{ success, data?, error?, code?, ...extras }`; `code`
 * carries the machine-readable business-rule code so the FE can branch (e.g.
 * BED_OCCUPIED → re-pick, DUPLICATE_ACTIVE_ADMISSION → link). Extra keys the
 * contract attaches to a coded error (bed_id, admission_id, ip_number, status,
 * dept_ids) are surfaced verbatim.
 */

import apiClient from './apiClient';
import { API_CONFIG } from '../../constants';

const BASE = '/api/organisation/admissions';
const enc = encodeURIComponent;

/**
 * Translate an IPD error into `{ error, code, ...extras }`. When the 4xx/5xx
 * `detail` is a `{code, message, ...}` dict, `code` + extras pass through so the
 * caller can react (409 re-pick, 409 link, 422 re-derive dept).
 */
function parseError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    const { code, message, ...extras } = detail;
    return { error: message || fallback, code, ...extras };
  }
  if (typeof detail === 'string') return { error: detail };
  switch (error?.response?.status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission to perform this action.' };
    case 404: return { error: 'Admission not found.' };
    case 503: return { error: 'Admissions are temporarily unavailable. Try again shortly.', code: 'IPD_TABLE_UNAVAILABLE' };
    default:  return { error: fallback };
  }
}

/**
 * §2.1 POST /admissions — create an admission (extended C1 payload).
 * A 200 (idempotent replay) and a 201 (created) are BOTH success; the caller
 * shows one toast either way (double-click cannot create two admissions).
 */
export const createAdmission = async (body) => {
  try {
    const response = await apiClient.post(BASE, body);
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[AdmissionAPI] create error:', error);
    return { success: false, ...parseError(error, 'Failed to create admission.') };
  }
};

/**
 * §2 GET /admissions?status= XOR ?patient_id= — the desk list. ONE query;
 * rows render off the denormalized fields (no per-row fan-out).
 *
 * `view` (diag-gst D-2, additive/optional) — pass `'picker'` for the IPD
 * page's money-free `AdmissionPickerView` projection (reachable with
 * VIEW_ADMISSION OR ACCESS_IPD). Omitted ⇒ today's response, byte-identical.
 */
export const listAdmissions = async ({ status, patient_id, limit, view } = {}) => {
  try {
    const params = {};
    if (status) params.status = status;
    if (patient_id) params.patient_id = patient_id;
    // C6a: optional server-side row cap (BE clamps 1..500). Omitted ⇒ 500 default
    // (byte-identical to prior behaviour). Only forwarded when a real number.
    if (limit != null && Number.isFinite(limit)) params.limit = limit;
    if (view) params.view = view;
    const response = await apiClient.get(BASE, { params });
    return {
      success: true,
      data: {
        admissions: response.data?.admissions || [],
        count: response.data?.count ?? (response.data?.admissions?.length || 0),
        // Additive, optional field — a bounded read hit its cap (more rows
        // exist than were returned). Absent on an older backend ⇒ false, so
        // callers built against this contract never crash/false-positive.
        truncated: response.data?.truncated ?? false,
        // admission-access C-4 — the server's word that the department wall
        // applied to this viewer. Strict-true: absent (older backend) = not walled.
        dept_scoped: response.data?.dept_scoped === true,
        // Server's word that every department of the viewer is deactivated (strict-true).
        dept_access_blocked: response.data?.dept_access_blocked === true,
        // admission-access ADD-1 — picker envelope only: the server's word that
        // the caller can be a treating doctor (drives the IPD page's My
        // admissions switch). Strict-true; absent (older backend) = false.
        viewer_can_be_treating: response.data?.viewer_can_be_treating === true,
      },
    };
  } catch (error) {
    console.error('[AdmissionAPI] list error:', error);
    return { success: false, ...parseError(error, 'Failed to load admissions.') };
  }
};

/**
 * discharge-perf §1 GET /admissions/discharges — the Discharged desk bucket,
 * ONE request per IST discharge-date window. `from`/`to` ('YYYY-MM-DD') are
 * omitted when blank (never sent as empty strings) ⇒ server default window
 * (last 30 days). The server ALWAYS echoes the resolved `period`.
 * 200 → { success, period:{from,to}, admissions, count, coverage:{truncated,cap} }
 * 4xx/5xx → { success:false, error, code, field, reason, message, ... } (422
 * INVALID_DATE_RANGE carries field + reason).
 */
export const listDischarges = async ({ from, to, limit } = {}) => {
  try {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (limit != null && Number.isFinite(limit)) params.limit = limit;
    const response = await apiClient.get(`${BASE}/discharges`, { params });
    const data = response.data || {};
    const admissions = data.admissions || [];
    return {
      success: true,
      period: data.period || null,
      admissions,
      count: data.count ?? admissions.length,
      coverage: data.coverage || { truncated: false, cap: null },
      dept_scoped: data.dept_scoped === true, // admission-access C-4 (absent = not walled)
      dept_access_blocked: data.dept_access_blocked === true,
      backdated_completed_today: data.backdated_completed_today || [], // bk-1865 (today window only)
    };
  } catch (error) {
    console.error('[AdmissionAPI] listDischarges error:', error);
    const parsed = parseError(error, 'Failed to load discharged admissions.');
    return { success: false, message: parsed.error, ...parsed };
  }
};

/**
 * GET /admissions/search?q=&limit= — org-wide, all-status, department-walled
 * desk search (any age). 200 → { success, data:{ admissions, count, truncated } }.
 * Failure → { success:false, status, code?, error } (422 QUERY_TOO_SHORT, 403,
 * 429, 503 IPD_TABLE_UNAVAILABLE); an aborted request → { success:false, aborted:true }.
 * `signal` (AbortSignal) lets the caller cancel a superseded query.
 */
export const searchAdmissions = async ({ q, limit } = {}, { signal } = {}) => {
  try {
    const params = { q };
    if (limit != null && Number.isFinite(limit)) params.limit = limit;
    const response = await apiClient.get(`${BASE}/search`, { params, signal });
    const data = response.data || {};
    const admissions = data.admissions || [];
    return {
      success: true,
      data: { admissions, count: data.count ?? admissions.length, truncated: data.truncated === true },
    };
  } catch (error) {
    if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
      return { success: false, aborted: true };
    }
    return { success: false, status: error?.response?.status, ...parseError(error, 'Search failed.') };
  }
};

/** §2 GET /admissions/{id} — single admission (AdmissionResponse §2.9). */
export const getAdmission = async (admissionId) => {
  try {
    const response = await apiClient.get(`${BASE}/${enc(admissionId)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionAPI] get error:', error);
    return { success: false, ...parseError(error, 'Failed to load admission.') };
  }
};

/**
 * C-2 GET /admissions/{id}/events — the admission timeline (AdmissionEventResponse[],
 * oldest-first). Lazy-fetched ONCE per inline-detail expand (never on list load).
 */
export const getAdmissionEvents = async (admissionId) => {
  try {
    const response = await apiClient.get(`${BASE}/${enc(admissionId)}/events`);
    return {
      success: true,
      data: {
        events: response.data?.events || [],
        count: response.data?.count ?? (response.data?.events?.length || 0),
      },
    };
  } catch (error) {
    console.error('[AdmissionAPI] getAdmissionEvents error:', error);
    return { success: false, ...parseError(error, 'Failed to load admission timeline.') };
  }
};

/** §2.2 POST /admissions/{id}/bed — attach a bed to an ADMITTED no-bed row. */
export const attachBed = async (admissionId, { bed_id, idempotency_key } = {}) => {
  try {
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/bed`, {
      bed_id,
      idempotency_key: idempotency_key || null,
    });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionAPI] attachBed error:', error);
    return { success: false, ...parseError(error, 'Failed to assign bed.') };
  }
};

/**
 * ADDENDUM-2 (day-1 S7/E-transfer, frozen contract) POST /admissions/{id}/transfer-bed
 * — move a BEDDED admission to a new vacant bed. NO client idempotency key —
 * the server's atomic transact + occupancy/current-bed guards ARE the
 * idempotency (a retry either re-races the same guards or is a no-op SAME_BED).
 * Envelope: { admission, from_bed, to_bed, tariff }.
 */
export const transferBed = async (admissionId, { to_bed_id, reason } = {}) => {
  try {
    const body = { to_bed_id };
    if (reason) body.reason = reason;
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/transfer-bed`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionAPI] transferBed error:', error);
    return { success: false, ...parseError(error, 'Failed to transfer the bed.') };
  }
};

/** §2.3 POST /admissions/{id}/primary-doctor — change-doctor intent. */
export const changePrimaryDoctor = async (admissionId, { primary_doctor_member_id, department_id } = {}) => {
  try {
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/primary-doctor`, {
      primary_doctor_member_id,
      department_id: department_id || null,
    });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionAPI] changePrimaryDoctor error:', error);
    return { success: false, ...parseError(error, 'Failed to change primary doctor.') };
  }
};

/**
 * admission-access C-6 POST /admissions/{id}/doctors — add ONE treating doctor
 * (body `{member_id}`). 200 = the full AdmissionResponse (already added = 200
 * no-op). Coded errors (`code`; the wire `message` is never UI copy):
 * DOCTOR_INVALID 422 · ALREADY_PRIMARY / CARE_TEAM_LIMIT (`limit`) /
 * CARE_TEAM_CONFLICT / ADMISSION_TERMINAL 409 · CARE_TEAM_NOT_PERMITTED /
 * OUT_OF_DEPARTMENT_SCOPE 403.
 */
export const addTreatingDoctor = async (admissionId, memberId) => {
  try {
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/doctors`, { member_id: memberId });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionAPI] addTreatingDoctor error:', error);
    return { success: false, ...parseError(error, 'Failed to add the doctor.') };
  }
};

/**
 * admission-access C-6 DELETE /admissions/{id}/doctors/{member_id} — remove an
 * ADDED doctor. 200 = the full AdmissionResponse (absent = 200 no-op); the
 * primary is refused 409 ALREADY_PRIMARY. Same 403/409 set as the add.
 */
export const removeTreatingDoctor = async (admissionId, memberId) => {
  try {
    const response = await apiClient.delete(`${BASE}/${enc(admissionId)}/doctors/${enc(memberId)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionAPI] removeTreatingDoctor error:', error);
    return { success: false, ...parseError(error, 'Failed to remove the doctor.') };
  }
};

/** §2.4 PATCH /admissions/{id} — capture-only detail update (payer/attender/MLC/diagnosis). */
export const patchAdmission = async (admissionId, body) => {
  try {
    const response = await apiClient.patch(`${BASE}/${enc(admissionId)}`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionAPI] patch error:', error);
    return { success: false, ...parseError(error, 'Failed to update admission.') };
  }
};

/**
 * R3W3 insurance capture — PATCH /admissions/{id}/insurance (C-1 frozen
 * contract). SPLIT from the general PATCH so the biller (COLLECT_DEPOSIT) can
 * write the insurer fields WITHOUT ADMIT_PATIENT; the body carries ONLY the
 * insurance fields (server rejects clinical fields with 422 extra=forbid).
 * Audit event + amount-lock ride the same server write path as the general PATCH.
 */
export const patchAdmissionInsurance = async (admissionId, body) => {
  try {
    const response = await apiClient.patch(`${BASE}/${enc(admissionId)}/insurance`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionAPI] patchInsurance error:', error);
    return { success: false, ...parseError(error, 'Failed to update insurance details.') };
  }
};

/** §2.5 PUT /admissions/{id}/plan-estimate — S6+S9 one sheet (full-replacement). */
export const updatePlanEstimate = async (admissionId, body) => {
  try {
    const response = await apiClient.put(`${BASE}/${enc(admissionId)}/plan-estimate`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionAPI] updatePlanEstimate error:', error);
    return { success: false, ...parseError(error, 'Failed to update estimate.') };
  }
};

/** §2.6 POST /admissions/{id}/transition — cancel/void (reason required for CANCELLED). */
export const transitionAdmission = async (admissionId, { to_status, reason } = {}) => {
  try {
    const body = { to_status };
    if (reason != null) body.reason = reason;
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/transition`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionAPI] transition error:', error);
    return { success: false, ...parseError(error, 'Failed to update admission status.') };
  }
};

/** §2.7 GET /bed-states — sparse BEDSTATE list; absent bed_id ≡ VACANT (FE merges). */
export const listBedStates = async () => {
  try {
    const response = await apiClient.get('/api/organisation/bed-states');
    return {
      success: true,
      data: {
        states: response.data?.states || [],
        count: response.data?.count ?? (response.data?.states?.length || 0),
      },
    };
  } catch (error) {
    console.error('[AdmissionAPI] listBedStates error:', error);
    return { success: false, ...parseError(error, 'Failed to load bed availability.') };
  }
};

/** §2.8 GET /admissions/{id}/estimate-pdf — absolute URL for a print/download link. */
export const estimatePdfUrl = (admissionId) =>
  `${API_CONFIG.BACKEND_URL}${BASE}/${enc(admissionId)}/estimate-pdf`;

const admissionApi = {
  createAdmission,
  listAdmissions,
  listDischarges,
  getAdmission,
  searchAdmissions,
  getAdmissionEvents,
  attachBed,
  transferBed,
  changePrimaryDoctor,
  addTreatingDoctor,
  removeTreatingDoctor,
  patchAdmission,
  patchAdmissionInsurance,
  updatePlanEstimate,
  transitionAdmission,
  listBedStates,
  estimatePdfUrl,
};

export default admissionApi;
